import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Send } from "lucide-react";
import { api, isApiError } from "@/lib/api";
import { useAuth } from "@/hooks/use-auth";
import { useHelpWidget } from "./help-widget-context";
import { cn } from "@/lib/utils";

/** Must match SUPPORT_CATEGORIES in worker/routes/editor.js. */
const CATEGORIES = ["Question", "Problem / bug", "Billing", "Feature request", "Other"] as const;
const MAX_LEN = 4000;

/**
 * "Message us" section of the Help widget: a manual contact form that emails
 * the support inbox (POST /api/editor/support). Replies land back on the user's
 * account email. Auto-context (workspace, plan, role, page, UA) is attached
 * server-side — the user just picks a category and writes.
 */
export function MessageSection({ onGoToHelp }: { onGoToHelp?: () => void }) {
  const { user } = useAuth();
  const { messageCategory: category, setMessageCategory: setCategory } = useHelpWidget();
  const [message, setMessage] = useState("");
  const isFeature = category === "Feature request";

  const send = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean }>("/editor/support", {
        category,
        message: message.trim(),
        page: typeof window !== "undefined" ? window.location.pathname : "",
      }),
  });

  if (send.isSuccess) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <CheckCircle2 className="h-10 w-10 text-primary" />
        <div>
          <p className="text-sm font-semibold">Message sent</p>
          <p className="mt-1 text-sm text-muted-foreground">
            We’ll reply to {user?.email ? <span className="text-foreground">{user.email}</span> : "your account email"},
            within 24–48 hours.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setMessage("");
            send.reset();
          }}
          className="mt-1 rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
        >
          Send another
        </button>
      </div>
    );
  }

  const disabled = send.isPending || !message.trim();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!disabled) send.mutate();
      }}
      className="flex h-full flex-col"
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {isFeature ? (
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            Have an idea for Editlock? Tell us what you’d like to see and the problem it would solve
            — we read every request. We’ll follow up at{" "}
            <span className="text-foreground">{user?.email || "your account email"}</span> if we
            need more detail.
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
            Check the{" "}
            <button
              type="button"
              onClick={onGoToHelp}
              className="font-medium text-foreground underline underline-offset-2"
            >
              Help
            </button>{" "}
            page first — you might find the answer there. If you still can’t find what you need, send
            us a message and we’ll get back to you at{" "}
            <span className="text-foreground">{user?.email || "your account email"}</span> within
            24–48 hours.
          </div>
        )}

        <div>
          <label className="mb-1.5 block text-xs font-medium text-muted-foreground">Topic</label>
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                  category === c
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground"
                )}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="support-message" className="mb-1.5 block text-xs font-medium text-muted-foreground">
            How can we help?
          </label>
          <textarea
            id="support-message"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
            rows={7}
            placeholder={
              isFeature
                ? "Describe the feature you’d like and the problem it would solve…"
                : "Describe your question or the problem you’re seeing…"
            }
            className="w-full resize-none rounded-lg border border-border bg-muted/40 p-3 text-sm outline-none transition-colors focus:border-ring"
          />
          <div className="mt-1 text-right text-[11px] text-muted-foreground">
            {message.length}/{MAX_LEN}
          </div>
        </div>

        {send.isError && (
          <p className="text-sm text-destructive">
            {isApiError(send.error) ? send.error.message : "Couldn’t send your message. Please try again."}
          </p>
        )}
      </div>

      <div className="border-t border-border p-3">
        <button
          type="submit"
          disabled={disabled}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {send.isPending ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>
  );
}
